import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Upload, CheckCircle2, XCircle, Loader2, AlertTriangle, Trash2, CreditCard, Lock, Plus, Landmark, Mail, Ban, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ISSUERS = ['AMEX', 'AXIS', 'DBS', 'HDFC', 'ICICI', 'IDFC', 'INDUSIND', 'KOTAK', 'RBL', 'SBI', 'YES BANK', 'OTHER'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function UploadPage() {
  const [tab, setTab] = useState('bank_accounts');
  const [cards, setCards] = useState([]);
  const [selectedCard, setSelectedCard] = useState('none');
  const [pdfPassword, setPdfPassword] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadQueue, setUploadQueue] = useState([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [cardForm, setCardForm] = useState({ card_name: '', issuer: 'AMEX', last_four: '', card_type: 'credit_card', statement_day: 1, region: 'india', pdf_password: '' });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, cardId: null, cardName: '' });
  const [fetchingEmail, setFetchingEmail] = useState(false);
  const [missingData, setMissingData] = useState(null);
  const fileRef = useRef(null);
  const abortRef = useRef(false);
  const failedFilesRef = useRef([]);

  const fetchData = useCallback(async () => {
    try {
      const [cardsRes, missingRes] = await Promise.all([
        axios.get(`${API}/cards`, { withCredentials: true }),
        axios.get(`${API}/statements/missing-from-april-2025`, { withCredentials: true }),
      ]);
      setCards(cardsRes.data);
      setMissingData(missingRes.data);
    } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const addCard = async () => {
    if (!cardForm.card_name || !cardForm.issuer) { toast.error('Name and issuer required'); return; }
    try {
      await axios.post(`${API}/cards`, { ...cardForm, card_type: tab === 'bank_accounts' ? 'bank_account' : 'credit_card' }, { withCredentials: true });
      toast.success(tab === 'bank_accounts' ? 'Bank account added' : 'Credit card added');
      setShowAddCard(false);
      setCardForm({ card_name: '', issuer: 'AMEX', last_four: '', card_type: 'credit_card', statement_day: 1, region: 'india', pdf_password: '' });
      fetchData();
    } catch (err) { toast.error('Failed to add'); }
  };

  const deleteCard = async (id, deleteTransactions) => {
    try {
      const { data } = await axios.delete(`${API}/cards/${id}?delete_transactions=${deleteTransactions}`, { withCredentials: true });
      toast.success(deleteTransactions ? `Deleted with ${data.transactions_deleted} transactions` : 'Deleted — transactions kept');
      setDeleteDialog({ open: false, cardId: null, cardName: '' });
      fetchData();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const tabType = tab === 'bank_accounts' ? 'bank_account' : 'credit_card';
  const tabCards = cards.filter(c => c.card_type === tabType);
  const isBankTab = tab === 'bank_accounts';

  const uploadSingleFile = async (file, queue, idx, total) => {
    const MAX_RETRIES = 3;
    for (let retry = 0; retry < MAX_RETRIES; retry++) {
      if (abortRef.current) return false;
      queue[idx] = { ...queue[idx], status: 'uploading', detail: retry > 0 ? `Retry ${retry}/${MAX_RETRIES - 1}...` : `Uploading ${idx + 1}/${total}...` };
      setUploadQueue([...queue]);

      const formData = new FormData();
      formData.append('file', file);
      if (selectedCard !== 'none') formData.append('card_id', selectedCard);
      if (pdfPassword) formData.append('pdf_password', pdfPassword);

      try {
        const { data } = await axios.post(`${API}/statements/upload`, formData, {
          withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60000,
        });
        queue[idx] = { ...queue[idx], status: 'processing', detail: 'AI parsing in progress...', stmtId: data.id };
        setUploadQueue([...queue]);

        let attempts = 0;
        const maxAttempts = 60;
        while (attempts < maxAttempts && !abortRef.current) {
          await new Promise(r => setTimeout(r, 3000));
          attempts++;
          try {
            const { data: stmt } = await axios.get(`${API}/statements/${data.id}`, { withCredentials: true });
            if (stmt.status === 'completed') {
              const junkInfo = stmt.junk_count ? ` (${stmt.junk_count} junk)` : '';
              const stmtDate = stmt.statement_date || '';
              let monthLabel = '';
              if (stmtDate && stmtDate.length >= 7) {
                const [sy, sm] = stmtDate.split('-');
                const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
                monthLabel = `${MONTHS[parseInt(sm) - 1]}-${sy.slice(2)} • `;
              }
              queue[idx] = { ...queue[idx], status: 'completed', detail: `${monthLabel}${stmt.transaction_count} txns from ${stmt.source}${junkInfo}` };
              setUploadQueue([...queue]);
              return true;
            } else if (stmt.status === 'failed') {
              queue[idx] = { ...queue[idx], status: 'failed', detail: stmt.error || 'Parsing failed' };
              setUploadQueue([...queue]);
              return false;
            }
            queue[idx] = { ...queue[idx], detail: `AI parsing... (${attempts * 3}s)` };
            setUploadQueue([...queue]);
          } catch { /* polling error, continue */ }
        }
        if (attempts >= maxAttempts && queue[idx].status === 'processing') {
          queue[idx] = { ...queue[idx], status: 'failed', detail: 'Timeout - check upload history' };
          setUploadQueue([...queue]);
        }
        return false;
      } catch (err) {
        const isNetworkErr = !err.response && (err.message === 'Network Error' || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED');
        if (isNetworkErr && retry < MAX_RETRIES - 1) {
          queue[idx] = { ...queue[idx], status: 'uploading', detail: 'Network error, retrying in 3s...' };
          setUploadQueue([...queue]);
          await new Promise(r => setTimeout(r, 3000));
          continue;
        }
        const msg = err.response?.data?.detail || err.message || 'Upload failed';
        queue[idx] = { ...queue[idx], status: 'failed', detail: msg.substring(0, 80) };
        setUploadQueue([...queue]);
        return false;
      }
    }
    return false;
  };

  const handleSkipMonth = async (accountKey, month) => {
    try {
      await axios.post(`${API}/cards/skip-month`, { account_key: accountKey, month }, { withCredentials: true });
      toast.success('Marked as no transactions');
      fetchData();
    } catch (err) { toast.error('Failed to skip month'); }
  };

  const handleUnskipMonth = async (accountKey, month) => {
    try {
      await axios.delete(`${API}/cards/skip-month`, { data: { account_key: accountKey, month }, withCredentials: true });
      toast.success('Month restored');
      fetchData();
    } catch (err) { toast.error('Failed to restore month'); }
  };

  const handleUpload = async (files) => {
    if (!files || files.length === 0) return;
    const pdfFiles = Array.from(files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (pdfFiles.length === 0) { toast.error('Only PDF files are supported'); return; }

    setUploading(true);
    abortRef.current = false;
    failedFilesRef.current = [];
    const queue = pdfFiles.map(f => ({ name: f.name, status: 'pending', detail: '', stmtId: null }));
    setUploadQueue([...queue]);

    let successCount = 0;
    for (let i = 0; i < pdfFiles.length; i++) {
      if (abortRef.current) break;
      if (i > 0) await new Promise(r => setTimeout(r, 1500));
      const ok = await uploadSingleFile(pdfFiles[i], queue, i, pdfFiles.length);
      if (ok) successCount++;
      else failedFilesRef.current.push({ file: pdfFiles[i], idx: i });
    }

    setUploading(false);
    fetchData();
    if (fileRef.current) fileRef.current.value = '';
    if (successCount > 0) toast.success(`Processed ${successCount}/${pdfFiles.length} statements`);
    if (failedFilesRef.current.length > 0) toast.error(`${failedFilesRef.current.length} files failed — use Retry button`);
  };

  const retryFailed = async () => {
    const failed = failedFilesRef.current;
    if (!failed || failed.length === 0) return;
    setUploading(true);
    abortRef.current = false;
    const queue = [...uploadQueue];
    let successCount = 0;
    const stillFailed = [];
    for (let j = 0; j < failed.length; j++) {
      if (abortRef.current) break;
      if (j > 0) await new Promise(r => setTimeout(r, 1500));
      const { file, idx } = failed[j];
      const ok = await uploadSingleFile(file, queue, idx, queue.length);
      if (ok) successCount++;
      else stillFailed.push({ file, idx });
    }
    failedFilesRef.current = stillFailed;
    setUploading(false);
    fetchData();
    if (successCount > 0) toast.success(`Retried: ${successCount} succeeded`);
    if (stillFailed.length > 0) toast.error(`${stillFailed.length} files still failing`);
  };

  const cancelUpload = () => { abortRef.current = true; };

  const fetchFromEmail = async (sender, folder, subjectFilter, overridePassword, mailbox) => {
    setFetchingEmail(true);
    try {
      const effectivePassword = overridePassword || pdfPassword || undefined;
      const endpoint = mailbox === 'kinntegra' ? `${API}/statements/fetch-email-kinntegra` : `${API}/statements/fetch-email`;
      const { data } = await axios.post(endpoint, null, {
        params: { sender_filter: sender, folder: folder || 'inbox', subject_filter: subjectFilter || 'eStatement', pdf_password: effectivePassword },
        withCredentials: true, timeout: 120000
      });
      if (data.uploaded > 0) {
        toast.success(`Fetched ${data.uploaded} new statements from email`);
        fetchData();
      } else if (data.skipped > 0) {
        toast.info(`All ${data.skipped} statements already uploaded`);
      } else {
        toast.info('No emails with PDF attachments found');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to fetch from email');
    } finally { setFetchingEmail(false); }
  };

  const hasMissingSources = missingData?.sources?.some(s => s.missing_count > 0);

  return (
    <div className="p-6 lg:p-8 space-y-5 max-w-[1200px]" data-testid="upload-page">
      {/* Header */}
      <div>
        <h2 className="font-heading text-2xl sm:text-3xl tracking-tight text-[#1C1C19]">Upload</h2>
        <p className="text-sm text-[#5C5C55] mt-1">Upload missing statements. Click upload below each missing month.</p>
      </div>

      {/* Tabs + Add button */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-[#E8E6DE] p-1 rounded-lg">
          <button data-testid="tab-bank-accounts"
            onClick={() => { setTab('bank_accounts'); setSelectedCard('none'); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'bank_accounts' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
            <Landmark size={15} /> Bank Accounts
          </button>
          <button data-testid="tab-credit-cards"
            onClick={() => { setTab('credit_cards'); setSelectedCard('none'); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${tab === 'credit_cards' ? 'bg-white text-[#1C1C19] shadow-sm' : 'text-[#5C5C55] hover:text-[#1C1C19]'}`}>
            <CreditCard size={15} /> Credit Cards
          </button>
        </div>
        <Button data-testid="add-card-btn" onClick={() => { setCardForm({ card_name: '', issuer: isBankTab ? 'DBS' : 'AMEX', last_four: '', card_type: isBankTab ? 'bank_account' : 'credit_card', statement_day: 1, region: 'india', pdf_password: '' }); setShowAddCard(true); }}
          className="bg-[#284A33] text-white hover:bg-[#1C3624] text-xs h-8">
          <Plus size={14} className="mr-1.5" /> Add {isBankTab ? 'Bank Account' : 'Credit Card'}
        </Button>
      </div>

      {/* Hidden file input */}
      <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden"
        onChange={(e) => handleUpload(e.target.files)} data-testid="upload-file-input" />

      {isBankTab ? (
        <>
          {/* Controls: Email fetch */}
          <div className="flex items-center gap-3 flex-wrap bg-[#F7F6F2] border border-[#E2E0D8] rounded-lg px-4 py-3">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-blue-600 shrink-0" />
              <span className="text-xs text-[#5C5C55]">Fetch from email:</span>
              {[
                { label: 'SCB', sender: 'Global.E-Statement-IN@sc.com', folder: '[Gmail]/All Mail', subject: 'eStatement', testId: 'fetch-email-sc' },
                { label: 'DBS', sender: 'estatement@dbs.com', folder: '[Gmail]/All Mail', subject: 'Account Statement for the month of', testId: 'fetch-email-dbs' },
                { label: 'IDFC', sender: 'statement@idfcfirst.bank.in', folder: '[Gmail]/All Mail', subject: 'IDFC FIRST Bank Account statement for the month of', testId: 'fetch-email-idfc' },
                { label: 'SBI', sender: 'cbssbi', folder: '[Gmail]/All Mail', subject: 'E-account statement for your SBI account', testId: 'fetch-email-sbi' },
                { label: 'SBI One-time', sender: 'cbssbi', folder: '[Gmail]/All Mail', subject: 'Statement for your SBI Account Number', testId: 'fetch-email-sbi-onetime', password: '51689160589' },
                { label: 'DBS FY', sender: 'estatement@dbs.com', folder: '[Gmail]/All Mail', subject: 'Account Statement for Financial Year', testId: 'fetch-email-dbs-fy', password: 'PUNI1605' },
                { label: 'IDFC (Kinntegra)', sender: 'statement@idfcfirst.bank.in', folder: '[Gmail]/All Mail', subject: 'IDFC FIRST Bank Account statement', testId: 'fetch-email-idfc-kinntegra', password: 'ANVPB5297J', mailbox: 'kinntegra' },
              ].map(bank => (
                <Button key={bank.label} data-testid={bank.testId}
                  onClick={() => fetchFromEmail(bank.sender, bank.folder, bank.subject, bank.password, bank.mailbox)}
                  disabled={fetchingEmail} variant="outline" size="sm"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50 text-xs h-7 px-2">
                  {fetchingEmail ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                  {bank.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Registered bank accounts */}
          {tabCards.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-[#5C5C55] uppercase tracking-wider font-medium">Registered Accounts</p>
              <div className="flex flex-wrap gap-2">
                {tabCards.map(card => (
                  <div key={card.id} className="flex items-center gap-2 bg-white border border-[#E2E0D8] rounded-md px-3 py-2 text-xs">
                    <Landmark size={12} className="text-[#284A33]" />
                    <span className="font-medium text-[#1C1C19]">{card.card_name}</span>
                    <span className="text-[9px] uppercase bg-[#E8E6DE] text-[#5C5C55] px-1 py-0.5 rounded">{card.issuer}</span>
                    {card.last_four && <span className="font-mono text-[#5C5C55]">A/c {card.last_four}</span>}
                    {card.pdf_password && <Lock size={9} className="text-[#5C5C55]" />}
                    <button onClick={() => setDeleteDialog({ open: true, cardId: card.id, cardName: card.card_name })}
                      className="text-[#B5B3A9] hover:text-red-500 transition-colors ml-1"><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bank-wise coverage table */}
          {missingData && missingData.sources.filter(s => (s.missing_count > 0 || (s.skipped_months && s.skipped_months.length > 0)) && s.type !== 'credit_card').length > 0 ? (
            <div className="space-y-4">
              {missingData.sources.filter(s => (s.missing_count > 0 || (s.skipped_months && s.skipped_months.length > 0)) && s.type !== 'credit_card').map((src) => {
                const missingMonths = [...src.missing_months].sort().reverse();
                const skippedMonths = [...(src.skipped_months || [])].sort().reverse();
                const rows = [];
                for (let i = 0; i < missingMonths.length; i += 6) {
                  rows.push(missingMonths.slice(i, i + 6));
                }
                const skippedRows = [];
                for (let i = 0; i < skippedMonths.length; i += 6) {
                  skippedRows.push(skippedMonths.slice(i, i + 6));
                }
                if (rows.length === 0 && skippedRows.length === 0) return null;
                const displayName = src.label || src.source;
                const keyId = `${src.source}-${src.account || ''}`.replace(/\s+/g, '-');
                const accountKey = src.account_key || `${src.source}|${src.account || ''}`;
                return (
                  <div key={keyId} className="bg-white border border-[#E2E0D8] rounded-md overflow-hidden">
                    {/* Bank header with bulk upload */}
                    <div className="px-5 py-3 border-b border-[#E2E0D8] bg-[#F7F6F2] flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Landmark size={14} className="text-[#284A33] shrink-0" />
                        <span className="text-sm font-medium text-[#1C1C19]">{displayName}</span>
                        {src.missing_count > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-orange-600">
                            <AlertTriangle size={10} /> {src.missing_count} pending
                          </span>
                        )}
                        {skippedMonths.length > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-[#8A8880]">
                            <Ban size={10} /> {skippedMonths.length} skipped
                          </span>
                        )}
                      </div>
                      <Button
                        data-testid={`bulk-upload-${keyId}`}
                        onClick={() => { if (!uploading) fileRef.current?.click(); }}
                        disabled={uploading}
                        className="bg-[#284A33] text-white hover:bg-[#1C3624] text-xs h-7 px-3">
                        <Upload size={12} className="mr-1.5" /> Bulk Upload
                      </Button>
                    </div>
                    {/* Pending months */}
                    {rows.length > 0 && (
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#E2E0D8]">
                            <th className="text-left px-5 py-2 text-[10px] font-semibold text-[#5C5C55] uppercase tracking-wider" colSpan={6}>Pending Months</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, ri) => (
                            <tr key={ri} className="border-b border-[#E2E0D8] last:border-b-0">
                              {row.map((m) => {
                                const [y, mo] = m.split('-');
                                const label = `${MONTH_SHORT[parseInt(mo) - 1]}-${y.slice(2)}`;
                                return (
                                  <td key={m} className="px-3 py-2.5 align-top">
                                    <div className="flex flex-col items-start gap-1">
                                      <span className="text-sm font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded border border-red-200"
                                        data-testid={`month-missing-${src.source.replace(/\s+/g, '-')}-${m}`}>
                                        {label}
                                      </span>
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => { if (!uploading) fileRef.current?.click(); }}
                                          disabled={uploading}
                                          className="flex items-center gap-1 text-[10px] text-[#284A33] font-medium hover:text-[#1C3624] px-1.5 py-0.5 rounded border border-[#284A33]/20 hover:border-[#284A33]/50 hover:bg-[#284A33]/5 transition-all disabled:opacity-40"
                                          data-testid={`upload-btn-${src.source.replace(/\s+/g, '-')}-${m}`}
                                        >
                                          <Upload size={10} /> Upload
                                        </button>
                                        <button
                                          onClick={() => handleSkipMonth(accountKey, m)}
                                          className="flex items-center gap-1 text-[10px] text-[#8A8880] font-medium hover:text-[#5C5C55] px-1.5 py-0.5 rounded border border-[#D5D2C6] hover:border-[#8A8880] hover:bg-[#F7F6F2] transition-all"
                                          data-testid={`skip-btn-${src.source.replace(/\s+/g, '-')}-${m}`}
                                        >
                                          <Ban size={10} /> No Txns
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                );
                              })}
                              {Array.from({ length: 6 - row.length }).map((_, i) => (
                                <td key={`empty-${i}`} className="px-3 py-2.5"></td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {/* Skipped months */}
                    {skippedRows.length > 0 && (
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#E2E0D8]">
                            <th className="text-left px-5 py-2 text-[10px] font-semibold text-[#8A8880] uppercase tracking-wider" colSpan={6}>No Transactions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {skippedRows.map((row, ri) => (
                            <tr key={ri} className="border-b border-[#E2E0D8] last:border-b-0">
                              {row.map((m) => {
                                const [y, mo] = m.split('-');
                                const label = `${MONTH_SHORT[parseInt(mo) - 1]}-${y.slice(2)}`;
                                return (
                                  <td key={m} className="px-3 py-2.5 align-top">
                                    <div className="flex flex-col items-start gap-1">
                                      <span className="text-sm font-medium text-[#8A8880] bg-[#F7F6F2] px-1.5 py-0.5 rounded border border-[#E2E0D8] line-through"
                                        data-testid={`month-skipped-${src.source.replace(/\s+/g, '-')}-${m}`}>
                                        {label}
                                      </span>
                                      <button
                                        onClick={() => handleUnskipMonth(accountKey, m)}
                                        className="flex items-center gap-1 text-[10px] text-[#8A8880] font-medium hover:text-[#284A33] px-1.5 py-0.5 rounded border border-[#E2E0D8] hover:border-[#284A33]/30 hover:bg-[#284A33]/5 transition-all"
                                        data-testid={`unskip-btn-${src.source.replace(/\s+/g, '-')}-${m}`}
                                      >
                                        <Undo2 size={10} /> Undo
                                      </button>
                                    </div>
                                  </td>
                                );
                              })}
                              {Array.from({ length: 6 - row.length }).map((_, i) => (
                                <td key={`empty-${i}`} className="px-3 py-2.5"></td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          ) : missingData ? (
            <div className="bg-white border border-[#E2E0D8] rounded-lg p-10 text-center">
              <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-3" />
              <p className="text-sm text-[#5C5C55]">All bank statements are up to date. No missing months.</p>
              <Button variant="outline" className="mt-3 text-xs border-[#284A33]/30 text-[#284A33]"
                onClick={() => fileRef.current?.click()} data-testid="upload-first-statement">
                <Upload size={14} className="mr-1.5" /> Upload Statement
              </Button>
            </div>
          ) : (
            <div className="p-8 text-center text-[#5C5C55] text-sm">Loading coverage data...</div>
          )}
        </>
      ) : (
        <>
          {/* Credit Cards tab */}
          {/* Controls: PDF password + Email fetch for CC */}
          <div className="flex items-center gap-3 flex-wrap bg-[#F7F6F2] border border-[#E2E0D8] rounded-lg px-4 py-3">
            <div className="flex items-center gap-2">
              <Mail size={14} className="text-blue-600 shrink-0" />
              <span className="text-xs text-[#5C5C55]">Fetch from email:</span>
              {[
                { label: 'Axis Magnus', sender: 'cc.statements@axis.bank.in', folder: '[Gmail]/All Mail', subject: 'Axis Bank Magnus Credit Card ending XX66', testId: 'fetch-email-axis-cc', password: 'PUNI8566' },
                { label: 'HDFC Tata Neu', sender: 'Emailstatements.cards@hdfcbank.bank.in', folder: '[Gmail]/All Mail', subject: 'Your HDFC Bank - Tata Neu Infinity HDFC Bank Credit Card Statement', testId: 'fetch-email-hdfc-cc', password: 'PUNI6240' },
                { label: 'ICICI', sender: 'credit_cards@icici.bank.in', folder: '[Gmail]/All Mail', subject: 'ICICI Bank Credit Card Statement for the period', testId: 'fetch-email-icici-cc', password: 'puni1605' },
                { label: 'IDFC Mayura', sender: 'statement@idfcfirst.bank.in', folder: '[Gmail]/All Mail', subject: 'Your Mayura Credit Card Statement', testId: 'fetch-email-idfc-mayura', password: 'ANVPB5297J', mailbox: 'kinntegra' },
                { label: 'IDFC Mayura (P)', sender: 'statement@idfcfirst.bank.in', folder: '[Gmail]/All Mail', subject: 'Your Mayura Credit Card Statement', testId: 'fetch-email-idfc-mayura-personal', password: '1605' },
              ].map(bank => (
                <Button key={bank.label} data-testid={bank.testId}
                  onClick={() => fetchFromEmail(bank.sender, bank.folder, bank.subject, bank.password, bank.mailbox)}
                  disabled={fetchingEmail} variant="outline" size="sm"
                  className="border-blue-200 text-blue-700 hover:bg-blue-50 text-xs h-7 px-2">
                  {fetchingEmail ? <Loader2 size={12} className="animate-spin mr-1" /> : null}
                  {bank.label}
                </Button>
              ))}
            </div>
          </div>

          {/* Registered credit cards */}
          {tabCards.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs text-[#5C5C55] uppercase tracking-wider font-medium">Registered Cards</p>
              <div className="flex flex-wrap gap-2">
                {tabCards.map(card => (
                  <div key={card.id} className="flex items-center gap-2 bg-white border border-[#E2E0D8] rounded-md px-3 py-2 text-xs">
                    <CreditCard size={12} className="text-[#284A33]" />
                    <span className="font-medium text-[#1C1C19]">{card.card_name}</span>
                    <span className="text-[9px] uppercase bg-[#E8E6DE] text-[#5C5C55] px-1 py-0.5 rounded">{card.issuer}</span>
                    {card.last_four && <span className="font-mono text-[#5C5C55]">****{card.last_four}</span>}
                    {card.pdf_password && <Lock size={9} className="text-[#5C5C55]" />}
                    <button onClick={() => setDeleteDialog({ open: true, cardId: card.id, cardName: card.card_name })}
                      className="text-[#B5B3A9] hover:text-red-500 transition-colors ml-1"><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CC-wise coverage table — same layout as bank tab */}
          {missingData && missingData.sources.filter(s => s.type === 'credit_card' && (s.missing_count > 0 || (s.skipped_months && s.skipped_months.length > 0))).length > 0 ? (
            <div className="space-y-4">
              {missingData.sources.filter(s => s.type === 'credit_card' && (s.missing_count > 0 || (s.skipped_months && s.skipped_months.length > 0))).map((src) => {
                const missingMonths = [...src.missing_months].sort().reverse();
                const skippedMonths = [...(src.skipped_months || [])].sort().reverse();
                const rows = [];
                for (let i = 0; i < missingMonths.length; i += 6) {
                  rows.push(missingMonths.slice(i, i + 6));
                }
                const skippedRows = [];
                for (let i = 0; i < skippedMonths.length; i += 6) {
                  skippedRows.push(skippedMonths.slice(i, i + 6));
                }
                if (rows.length === 0 && skippedRows.length === 0) return null;
                const displayName = src.label || src.source;
                const keyId = `${src.source}-${src.account || ''}`.replace(/\s+/g, '-');
                const accountKey = src.account_key || `${src.source}|${src.account || ''}`;
                return (
                  <div key={keyId} className="bg-white border border-[#E2E0D8] rounded-md overflow-hidden">
                    <div className="px-5 py-3 border-b border-[#E2E0D8] bg-[#F7F6F2] flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <CreditCard size={14} className="text-[#284A33] shrink-0" />
                        <span className="text-sm font-medium text-[#1C1C19]">{displayName}</span>
                        {src.missing_count > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-orange-600">
                            <AlertTriangle size={10} /> {src.missing_count} pending
                          </span>
                        )}
                        {skippedMonths.length > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-[#8A8880]">
                            <Ban size={10} /> {skippedMonths.length} skipped
                          </span>
                        )}
                      </div>
                      <Button
                        data-testid={`bulk-upload-cc-${keyId}`}
                        onClick={() => { if (!uploading) fileRef.current?.click(); }}
                        disabled={uploading}
                        className="bg-[#284A33] text-white hover:bg-[#1C3624] text-xs h-7 px-3">
                        <Upload size={12} className="mr-1.5" /> Bulk Upload
                      </Button>
                    </div>
                    {/* Pending months */}
                    {rows.length > 0 && (
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#E2E0D8]">
                            <th className="text-left px-5 py-2 text-[10px] font-semibold text-[#5C5C55] uppercase tracking-wider" colSpan={6}>Pending Months</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, ri) => (
                            <tr key={ri} className="border-b border-[#E2E0D8] last:border-b-0">
                              {row.map((m) => {
                                const [y, mo] = m.split('-');
                                const label = `${MONTH_SHORT[parseInt(mo) - 1]}-${y.slice(2)}`;
                                return (
                                  <td key={m} className="px-3 py-2.5 align-top">
                                    <div className="flex flex-col items-start gap-1">
                                      <span className="text-sm font-medium text-red-400 bg-red-50 px-1.5 py-0.5 rounded border border-red-200"
                                        data-testid={`month-missing-cc-${src.source.replace(/\s+/g, '-')}-${m}`}>
                                        {label}
                                      </span>
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => { if (!uploading) fileRef.current?.click(); }}
                                          disabled={uploading}
                                          className="flex items-center gap-1 text-[10px] text-[#284A33] font-medium hover:text-[#1C3624] px-1.5 py-0.5 rounded border border-[#284A33]/20 hover:border-[#284A33]/50 hover:bg-[#284A33]/5 transition-all disabled:opacity-40"
                                          data-testid={`upload-btn-cc-${src.source.replace(/\s+/g, '-')}-${m}`}
                                        >
                                          <Upload size={10} /> Upload
                                        </button>
                                        <button
                                          onClick={() => handleSkipMonth(accountKey, m)}
                                          className="flex items-center gap-1 text-[10px] text-[#8A8880] font-medium hover:text-[#5C5C55] px-1.5 py-0.5 rounded border border-[#D5D2C6] hover:border-[#8A8880] hover:bg-[#F7F6F2] transition-all"
                                          data-testid={`skip-btn-cc-${src.source.replace(/\s+/g, '-')}-${m}`}
                                        >
                                          <Ban size={10} /> No Txns
                                        </button>
                                      </div>
                                    </div>
                                  </td>
                                );
                              })}
                              {Array.from({ length: 6 - row.length }).map((_, i) => (
                                <td key={`empty-${i}`} className="px-3 py-2.5"></td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                    {/* Skipped months */}
                    {skippedRows.length > 0 && (
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-[#E2E0D8]">
                            <th className="text-left px-5 py-2 text-[10px] font-semibold text-[#8A8880] uppercase tracking-wider" colSpan={6}>No Transactions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {skippedRows.map((row, ri) => (
                            <tr key={ri} className="border-b border-[#E2E0D8] last:border-b-0">
                              {row.map((m) => {
                                const [y, mo] = m.split('-');
                                const label = `${MONTH_SHORT[parseInt(mo) - 1]}-${y.slice(2)}`;
                                return (
                                  <td key={m} className="px-3 py-2.5 align-top">
                                    <div className="flex flex-col items-start gap-1">
                                      <span className="text-sm font-medium text-[#8A8880] bg-[#F7F6F2] px-1.5 py-0.5 rounded border border-[#E2E0D8] line-through"
                                        data-testid={`month-skipped-cc-${src.source.replace(/\s+/g, '-')}-${m}`}>
                                        {label}
                                      </span>
                                      <button
                                        onClick={() => handleUnskipMonth(accountKey, m)}
                                        className="flex items-center gap-1 text-[10px] text-[#8A8880] font-medium hover:text-[#284A33] px-1.5 py-0.5 rounded border border-[#E2E0D8] hover:border-[#284A33]/30 hover:bg-[#284A33]/5 transition-all"
                                        data-testid={`unskip-btn-cc-${src.source.replace(/\s+/g, '-')}-${m}`}
                                      >
                                        <Undo2 size={10} /> Undo
                                      </button>
                                    </div>
                                  </td>
                                );
                              })}
                              {Array.from({ length: 6 - row.length }).map((_, i) => (
                                <td key={`empty-${i}`} className="px-3 py-2.5"></td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                );
              })}
            </div>
          ) : missingData ? (
            <div className="bg-white border border-[#E2E0D8] rounded-lg p-10 text-center">
              <CheckCircle2 size={40} className="text-emerald-500 mx-auto mb-3" />
              <p className="text-sm text-[#5C5C55]">All credit card statements are up to date. No missing months.</p>
              <Button variant="outline" className="mt-3 text-xs border-[#284A33]/30 text-[#284A33]"
                onClick={() => fileRef.current?.click()} data-testid="upload-first-cc-statement">
                <Upload size={14} className="mr-1.5" /> Upload Statement
              </Button>
            </div>
          ) : (
            <div className="p-8 text-center text-[#5C5C55] text-sm">Loading coverage data...</div>
          )}
        </>
      )}

      {/* Upload Progress — sticky banner during active upload */}
      {uploadQueue.length > 0 && (() => {
        const completed = uploadQueue.filter(q => q.status === 'completed').length;
        const failed = uploadQueue.filter(q => q.status === 'failed').length;
        const total = uploadQueue.length;
        const pct = total > 0 ? Math.round(((completed + failed) / total) * 100) : 0;
        const activeItem = uploadQueue.find(q => q.status === 'uploading' || q.status === 'processing');
        return (
          <div className={`${uploading ? 'sticky top-0 z-30' : ''} space-y-2 bg-white border border-[#E2E0D8] rounded-lg p-4 shadow-sm`} data-testid="upload-queue">
            {/* Overall progress header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {uploading ? <Loader2 size={16} className="text-[#284A33] animate-spin" /> : <CheckCircle2 size={16} className="text-emerald-600" />}
                <span className="text-sm font-medium text-[#1C1C19]">
                  {uploading ? `Processing ${completed + 1} of ${total}...` : `Upload complete — ${completed} succeeded${failed > 0 ? `, ${failed} failed` : ''}`}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-xs font-mono text-[#5C5C55]">{completed}/{total}</span>
                {uploading && (
                  <button onClick={cancelUpload} className="text-xs text-red-500 hover:text-red-700 font-medium" data-testid="cancel-upload">Cancel</button>
                )}
                {!uploading && failedFilesRef.current.length > 0 && (
                  <button onClick={retryFailed} data-testid="retry-failed-btn" className="text-xs font-medium text-[#D36135] hover:text-[#B5502A] underline">
                    Retry {failedFilesRef.current.length} failed
                  </button>
                )}
                {!uploading && (
                  <button onClick={() => setUploadQueue([])} className="text-xs text-[#5C5C55] hover:text-[#1C1C19]">Dismiss</button>
                )}
              </div>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-[#E8E6DE] rounded-full h-1.5">
              <div className={`h-1.5 rounded-full transition-all duration-500 ${failed > 0 && !uploading ? 'bg-orange-400' : 'bg-[#284A33]'}`}
                style={{ width: `${pct}%` }} />
            </div>
            {/* Active file status */}
            {activeItem && (
              <div className="flex items-center gap-2 text-xs text-[#5C5C55] bg-[#F7F6F2] rounded px-3 py-1.5">
                <Loader2 size={12} className="animate-spin text-[#284A33]" />
                <span className="font-medium text-[#1C1C19] truncate">{activeItem.name}</span>
                <span className="ml-auto shrink-0">{activeItem.detail}</span>
              </div>
            )}
            {/* File list — compact, scrollable */}
            <div className="max-h-[200px] overflow-y-auto space-y-1">
              {uploadQueue.map((item, i) => (
                <div key={i} className={`flex items-center gap-2 rounded px-3 py-1.5 text-xs ${
                  item.status === 'failed' ? 'bg-red-50' :
                  item.status === 'completed' ? 'bg-emerald-50' :
                  item.status === 'processing' || item.status === 'uploading' ? 'bg-[#284A33]/5' : 'bg-[#F7F6F2]'
                }`}>
                  {item.status === 'completed' && <CheckCircle2 size={12} className="text-emerald-600 shrink-0" />}
                  {item.status === 'failed' && <XCircle size={12} className="text-red-500 shrink-0" />}
                  {item.status === 'pending' && <div className="w-3 h-3 rounded-full border-2 border-[#D5D2C6] shrink-0" />}
                  {(item.status === 'processing' || item.status === 'uploading') && <Loader2 size={12} className="text-[#284A33] animate-spin shrink-0" />}
                  <span className={`truncate ${item.status === 'failed' ? 'text-red-700' : 'text-[#1C1C19]'} font-medium`}>{item.name}</span>
                  {item.detail && <span className={`ml-auto shrink-0 ${item.status === 'completed' ? 'text-emerald-700' : 'text-[#5C5C55]'}`}>{item.detail}</span>}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Add Card/Account Dialog */}
      <Dialog open={showAddCard} onOpenChange={setShowAddCard}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add {isBankTab ? 'Bank Account' : 'Credit Card'}</DialogTitle>
            <DialogDescription>Register to track statement coverage</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Name</Label>
              <Input data-testid="card-name-input" value={cardForm.card_name} onChange={e => setCardForm({...cardForm, card_name: e.target.value})}
                placeholder={isBankTab ? 'e.g. DBS Savings' : 'e.g. AMEX Platinum'} className="border-[#E2E0D8]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">{isBankTab ? 'Bank' : 'Issuer'}</Label>
                <Select value={cardForm.issuer} onValueChange={v => setCardForm({...cardForm, issuer: v})}>
                  <SelectTrigger className="border-[#E2E0D8]"><SelectValue /></SelectTrigger>
                  <SelectContent>{ISSUERS.map(i => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">{isBankTab ? 'Account Number' : 'Last 4 Digits'}</Label>
                <Input data-testid="card-last-four-input" value={cardForm.last_four}
                  onChange={e => setCardForm({...cardForm, last_four: isBankTab ? e.target.value : e.target.value.slice(0,4)})}
                  placeholder={isBankTab ? 'e.g. 881017802697' : '1234'} maxLength={isBankTab ? 20 : 4} className="border-[#E2E0D8] font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Statement Day</Label>
                <Input type="number" min={1} max={31} value={cardForm.statement_day}
                  onChange={e => setCardForm({...cardForm, statement_day: parseInt(e.target.value) || 1})} className="border-[#E2E0D8]" />
              </div>
              <div>
                <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">Region</Label>
                <Select value={cardForm.region} onValueChange={v => setCardForm({...cardForm, region: v})}>
                  <SelectTrigger className="border-[#E2E0D8]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="india">India</SelectItem>
                    <SelectItem value="uae">UAE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isBankTab && (
              <div>
                <Label className="text-[#5C5C55] text-xs tracking-[0.15em] uppercase mb-1.5 block">PDF Password</Label>
                <Input type="password" placeholder="Password to unlock statement PDFs" data-testid="card-pdf-password"
                  value={cardForm.pdf_password} onChange={e => setCardForm({...cardForm, pdf_password: e.target.value})} className="border-[#E2E0D8]" />
                <p className="text-[10px] text-[#5C5C55] mt-1">Auto-used when uploading statements for this account.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowAddCard(false)}>Cancel</Button>
            <Button onClick={addCard} data-testid="save-card-btn" className="bg-[#284A33] text-white hover:bg-[#1C3624]">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onOpenChange={(o) => !o && setDeleteDialog({ open: false, cardId: null, cardName: '' })}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete {deleteDialog.cardName}?</DialogTitle>
            <DialogDescription>What would you like to do with linked transactions?</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Button data-testid="delete-card-keep-txns" onClick={() => deleteCard(deleteDialog.cardId, false)}
              variant="outline" className="w-full justify-start h-auto py-3 px-4 border-[#E2E0D8] hover:border-[#284A33]">
              <div className="text-left">
                <p className="font-medium text-sm text-[#1C1C19]">Keep transactions</p>
                <p className="text-xs text-[#5C5C55] mt-0.5">Delete only the card/account. Transactions preserved.</p>
              </div>
            </Button>
            <Button data-testid="delete-card-delete-txns" onClick={() => deleteCard(deleteDialog.cardId, true)}
              variant="outline" className="w-full justify-start h-auto py-3 px-4 border-red-200 hover:border-red-400 hover:bg-red-50 text-red-700">
              <div className="text-left">
                <p className="font-medium text-sm">Delete everything</p>
                <p className="text-xs text-red-500 mt-0.5">Delete card, statements, and all transactions permanently.</p>
              </div>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDialog({ open: false, cardId: null, cardName: '' })}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
